/**
 * Base64 decoding utilities for audit log display.
 *
 * These utilities help detect and decode base64-encoded strings that appear
 * in audit log data, such as event names that are base64-encoded.
 */

/**
 * Minimum length to consider a string as potentially base64-encoded.
 * Short strings like "purchase" or "signup" are unlikely to be base64.
 */
export const MIN_BASE64_LENGTH = 20;

/**
 * Pattern for base64 character set (alphanumeric + /+=)
 */
const BASE64_PATTERN = /^[\d+/=A-Za-z]+$/;

/**
 * Pattern for base64-specific characters that wouldn't appear in normal words
 */
const BASE64_SPECIFIC_CHARS = /[+/=]/;

/**
 * Pattern for simple word characters (not likely to be base64)
 */
const SIMPLE_WORD_PATTERN = /^[\w-]+$/;

/**
 * Pattern for printable ASCII and UTF-8 characters
 */
const PRINTABLE_PATTERN = /^[\x20-\x7E\u00A0-\uFFFF]*$/;

/**
 * Check if a string looks like it might be base64-encoded.
 *
 * A string is considered potentially base64 if:
 * - It contains base64-specific characters (+, /, =), OR
 * - It's longer than MIN_BASE64_LENGTH and matches base64 character set
 *
 * @param value - The string to check
 * @returns true if the string looks like base64
 */
export function looksLikeBase64(value: string): boolean {
  return BASE64_SPECIFIC_CHARS.test(value) || (value.length > MIN_BASE64_LENGTH && BASE64_PATTERN.test(value));
}

/**
 * Check if a string is a simple word that shouldn't be decoded.
 *
 * Simple words are short strings without base64-specific characters
 * that are likely just regular text like "purchase" or "signup".
 *
 * @param value - The string to check
 * @returns true if the string is a simple word
 */
export function isSimpleWord(value: string): boolean {
  return !BASE64_SPECIFIC_CHARS.test(value) && value.length <= MIN_BASE64_LENGTH && SIMPLE_WORD_PATTERN.test(value);
}

/**
 * Try to decode a base64-encoded string.
 *
 * This function will:
 * 1. Return the original value if it's a simple word (not base64)
 * 2. Attempt to decode if it looks like base64
 * 3. Verify the decoded result is printable text (not binary garbage)
 * 4. Verify the roundtrip encoding matches the original
 * 5. Return the original value if decoding fails or produces invalid results
 *
 * @param value - The string to potentially decode
 * @returns The decoded string if successful, or the original value
 *
 * @example
 * tryDecodeBase64('cHVyY2hhc2U=') // 'purchase'
 * tryDecodeBase64('purchase') // 'purchase' (not decoded, simple word)
 * tryDecodeBase64('ABC123') // 'ABC123' (not decoded, short and no base64 chars)
 */
export function tryDecodeBase64(value: string): string {
  // Simple words don't need decoding
  if (isSimpleWord(value)) {
    return value;
  }

  // Only attempt decode if it looks like base64
  if (!looksLikeBase64(value) || !BASE64_PATTERN.test(value)) {
    return value;
  }

  try {
    const decoded = atob(value);

    // Verify the decoded string is valid printable text (not binary garbage)
    if (!PRINTABLE_PATTERN.test(decoded)) {
      return value;
    }

    // Verify roundtrip works (ensures it was actually base64)
    if (btoa(decoded) !== value) {
      return value;
    }

    return decoded;
  } catch {
    // Not valid base64
    return value;
  }
}
